/**
 * Утилиты для работы с Variable Fonts
 * Парсинг fvar и name таблиц TTF/OTF для определения осей вариации
 */

export interface FontAxis {
  tag: string;       // 'wght', 'wdth', 'slnt', etc.
  name: string;      // Human-readable: 'Weight', 'Width', etc.
  min: number;
  max: number;
  default: number;
}

/** Стандартные оси и их русские названия */
const REGISTERED_AXES: Record<string, string> = {
  wght: 'Толщина (Weight)',
  wdth: 'Ширина (Width)',
  ital: 'Курсив (Italic)',
  slnt: 'Наклон (Slant)',
  opsz: 'Оптический размер (Optical Size)',
  GRAD: 'Градация (Grade)',
  CASL: 'Casual',
  CRSV: 'Cursive',
  FILL: 'Заливка (Fill)',
  MONO: 'Monospace',
  SOFT: 'Мягкость (Softness)',
  WONK: 'Wonky',
  XHGT: 'Высота строчных (x-Height)',
  XTRA: 'X Transparent',
  YTAS: 'Y Transparent Ascender',
};

/**
 * Определяет, является ли файл шрифта variable font, и возвращает оси
 * Поддерживает TTF и OTF (не WOFF/WOFF2 — они сжаты)
 */
export async function parseVariableFontAxes(
  source: File | ArrayBuffer | string
): Promise<FontAxis[]> {
  try {
    let buffer: ArrayBuffer;

    if (source instanceof File) {
      buffer = await source.arrayBuffer();
    } else if (source instanceof ArrayBuffer) {
      buffer = source;
    } else if (typeof source === 'string') {
      // URL или data URL
      if (source.startsWith('data:')) {
        // data URL — декодируем base64
        const base64 = source.split(',')[1];
        const binary = atob(base64);
        buffer = new ArrayBuffer(binary.length);
        const view = new Uint8Array(buffer);
        for (let i = 0; i < binary.length; i++) {
          view[i] = binary.charCodeAt(i);
        }
      } else {
        // Обычный URL — скачиваем
        const response = await fetch(source);
        buffer = await response.arrayBuffer();
      }
    } else {
      return [];
    }

    return parseFvarFromBuffer(buffer);
  } catch (error) {
    console.warn('⚠️ Не удалось распарсить variable font:', error);
    return [];
  }
}

/**
 * Парсит fvar таблицу из ArrayBuffer TTF/OTF файла
 */
function parseFvarFromBuffer(buffer: ArrayBuffer): FontAxis[] {
  const data = new DataView(buffer);

  // Проверяем сигнатуру
  const sfVersion = data.getUint32(0);
  
  // 0x00010000 = TrueType, 0x4F54544F = 'OTTO' (CFF/OpenType)
  // 0x74746366 = 'ttcf' (TrueType Collection)
  let tableOffset = 12;
  let numTables: number;

  if (sfVersion === 0x74746366) {
    // TTC — берем первый шрифт
    const majorVersion = data.getUint16(4);
    const numFonts = data.getUint32(8);
    if (numFonts === 0) return [];
    
    const firstFontOffset = data.getUint32(12);
    numTables = data.getUint16(firstFontOffset + 4);
    tableOffset = firstFontOffset + 12;
  } else if (sfVersion === 0x00010000 || sfVersion === 0x4F54544F) {
    numTables = data.getUint16(4);
  } else {
    // Неподдерживаемый формат (WOFF/WOFF2 и т.д.)
    console.log('⚠️ Формат шрифта не поддерживается для парсинга осей');
    return [];
  }

  // Ищем таблицы fvar и name
  let fvarOffset = -1;
  let fvarLength = 0;
  let nameOffset = -1;
  let nameLength = 0;

  for (let i = 0; i < numTables; i++) {
    const recordOffset = tableOffset + i * 16;
    const tag = String.fromCharCode(
      data.getUint8(recordOffset),
      data.getUint8(recordOffset + 1),
      data.getUint8(recordOffset + 2),
      data.getUint8(recordOffset + 3)
    );
    
    if (tag === 'fvar') {
      fvarOffset = data.getUint32(recordOffset + 8);
      fvarLength = data.getUint32(recordOffset + 12);
    }
    if (tag === 'name') {
      nameOffset = data.getUint32(recordOffset + 8);
      nameLength = data.getUint32(recordOffset + 12);
    }
  }

  if (fvarOffset === -1) {
    // Нет fvar таблицы — не variable font
    return [];
  }

  // Парсим name таблицу для получения имён осей
  const nameMap = nameOffset !== -1 ? parseNameTable(data, nameOffset) : new Map<number, string>();

  // Парсим fvar таблицу
  const axesArrayOffset = data.getUint16(fvarOffset + 4);
  const axisCount = data.getUint16(fvarOffset + 8);
  const axisSize = data.getUint16(fvarOffset + 10);

  const axes: FontAxis[] = [];

  for (let i = 0; i < axisCount; i++) {
    const axisOffset = fvarOffset + axesArrayOffset + i * axisSize;

    const tag = String.fromCharCode(
      data.getUint8(axisOffset),
      data.getUint8(axisOffset + 1),
      data.getUint8(axisOffset + 2),
      data.getUint8(axisOffset + 3)
    );

    // Fixed 16.16 format
    const minValue = data.getInt32(axisOffset + 4) / 65536;
    const defaultValue = data.getInt32(axisOffset + 8) / 65536;
    const maxValue = data.getInt32(axisOffset + 12) / 65536;
    const axisNameID = data.getUint16(axisOffset + 18);

    // Определяем имя оси
    let name = REGISTERED_AXES[tag] || nameMap.get(axisNameID) || tag;

    axes.push({ tag, name, min: minValue, max: maxValue, default: defaultValue });
  }

  console.log(`✅ Найдено ${axes.length} осей variable font:`, axes);
  return axes;
}

/**
 * Парсит name таблицу TTF для получения строк по nameID
 */
function parseNameTable(data: DataView, offset: number): Map<number, string> {
  const nameMap = new Map<number, string>();
  
  try {
    const count = data.getUint16(offset + 2);
    const storageOffset = offset + data.getUint16(offset + 4);

    for (let i = 0; i < count; i++) {
      const recordOffset = offset + 6 + i * 12;
      const platformID = data.getUint16(recordOffset);
      const encodingID = data.getUint16(recordOffset + 2);
      const languageID = data.getUint16(recordOffset + 4);
      const nameID = data.getUint16(recordOffset + 6);
      const length = data.getUint16(recordOffset + 8);
      const stringOffset = data.getUint16(recordOffset + 10);

      // Уже есть запись — пропускаем (приоритет первой найденной)
      if (nameMap.has(nameID)) continue;

      const strStart = storageOffset + stringOffset;

      if (platformID === 3 && encodingID === 1) {
        // Windows Unicode BMP — UTF-16BE
        let str = '';
        for (let j = 0; j < length; j += 2) {
          str += String.fromCharCode(data.getUint16(strStart + j));
        }
        nameMap.set(nameID, str);
      } else if (platformID === 1 && encodingID === 0) {
        // Macintosh Roman — примерно ASCII
        let str = '';
        for (let j = 0; j < length; j++) {
          str += String.fromCharCode(data.getUint8(strStart + j));
        }
        if (!nameMap.has(nameID)) {
          nameMap.set(nameID, str);
        }
      }
    }
  } catch (e) {
    console.warn('⚠️ Ошибка парсинга name таблицы:', e);
  }

  return nameMap;
}

/**
 * Формирует CSS font-variation-settings строку из Record
 * Используется для Canvas ctx.fontVariationSettings и CSS inline styles
 */
export function buildFontVariationCSS(settings: Record<string, number>): string {
  return Object.entries(settings)
    .map(([tag, value]) => `"${tag}" ${value}`)
    .join(', ');
}

/**
 * Определяет шаг слайдера для конкретной оси
 */
export function getAxisStep(tag: string, min: number, max: number): number {
  const range = max - min;
  if (tag === 'ital') return 1; // 0 или 1
  if (tag === 'wght') return 1; // целые значения для weight
  if (range <= 2) return 0.01;
  if (range <= 10) return 0.1;
  if (range <= 100) return 1;
  return 1;
}

/**
 * Проверяет, является ли шрифт variable font (быстрая проверка)
 */
export function isVariableFont(axes: FontAxis[]): boolean {
  return axes.length > 0;
}

/**
 * Применяет font-variation-settings к Canvas 2D context
 * Строит правильную строку ctx.font с учётом weight из вариаций
 * и устанавливает ctx.fontVariationSettings если поддерживается
 */
export function applyFontVariationToCanvas(
  ctx: CanvasRenderingContext2D,
  fontSize: number,
  fontFamily: string,
  fontVariationSettings?: Record<string, number>,
  fontBold?: boolean // fallback для обратной совместимости
): void {
  // Определяем font-weight
  let weight: string | number = 'normal';
  if (fontVariationSettings?.wght) {
    weight = fontVariationSettings.wght;
  } else if (fontBold) {
    weight = 'bold';
  }

  // Определяем font-style
  let style = 'normal';
  if (fontVariationSettings?.ital && fontVariationSettings.ital > 0.5) {
    style = 'italic';
  } else if (fontVariationSettings?.slnt && fontVariationSettings.slnt !== 0) {
    style = `oblique ${Math.abs(fontVariationSettings.slnt)}deg`;
  }

  // Строим строку ctx.font
  // Кавычки: кастомные имена с точкой/запятой (CustomFont_ofont.ru_Impact_…)
  // без кавычек делают шорткат невалидным → ctx.font не применяется (дефолт 10px).
  ctx.font = `${style} ${weight} ${fontSize}px "${fontFamily}", sans-serif`;

  // Применяем все оси через fontVariationSettings если поддерживается
  if (fontVariationSettings && Object.keys(fontVariationSettings).length > 0) {
    const variationCSS = buildFontVariationCSS(fontVariationSettings);
    try {
      (ctx as any).fontVariationSettings = variationCSS;
    } catch (e) {
      // Не поддерживается в этом браузере — weight/style из ctx.font всё равно сработает
      console.warn('⚠️ ctx.fontVariationSettings не поддерживается, используем fallback через ctx.font');
    }
  }
}

/**
 * Строит строку font для ctx.font с учётом вариаций (для measureText и т.д.)
 */
export function buildCanvasFontString(
  fontSize: number,
  fontFamily: string,
  fontVariationSettings?: Record<string, number>,
  fontBold?: boolean
): string {
  let weight: string | number = 'normal';
  if (fontVariationSettings?.wght) {
    weight = fontVariationSettings.wght;
  } else if (fontBold) {
    weight = 'bold';
  }

  let style = 'normal';
  if (fontVariationSettings?.ital && fontVariationSettings.ital > 0.5) {
    style = 'italic';
  }

  return `${style} ${weight} ${fontSize}px "${fontFamily}", sans-serif`;
}